import React, { useCallback, useState } from 'react';
import { ReportAnswers } from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackReport';
import { createContext, useContext, useEffect } from 'react';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { getReportAnswers, ReportAnswersResponse } from './api';
import { useClient } from 'shared/apollo';
import { useParams } from 'react-router';
import { mapReportBuilderAnswersToFeedbackAnswer } from '../ReportBuilder/Components/Review/ReportRender';
import { SuccessResponse } from '../ReportsBeta/NewReport/api';
import {
  FeedbackQuestion,
  UpdateAnswer,
  updateAnswer,
} from '../NPOFeedback/MakeEdits/api';
import { FeedbackSectionId } from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';
import { requestInit, RequestState } from 'shared/auth/hooks/useRequestState';
import { Answer } from 'donor/portal/pages/FeedbackDashboard/Feedback/api';
import { fixImpactDetailAnswers } from 'donor/portal/pages/FeedbackDashboard/Feedback/SectionFeedback';
import AppUnauthenticated from 'shared/components/AppUnauthenticated';

const orderAnswerIds = [40015, 50001, 50002];
export const orderAnswers = (answers: Answer[]) => {
  const order: Answer[] = [];
  const result: Answer[] = [];

  for (const a of answers) {
    if (orderAnswerIds.includes(a.questionId)) {
      order.push(a);
    } else {
      result.push(a);
    }
  }

  return [...result, ...order.sort((a, b) => a.questionId - b.questionId)];
};

export interface FeedbackQuestionsWithSectionsById {
  [questionId: number]:
    | (FeedbackQuestion & { sectionId: FeedbackSectionId })
    | undefined;
}

const mapAnswersToQuestions = (
  answers: Answer[],
): FeedbackQuestionsWithSectionsById => {
  const questionsById: FeedbackQuestionsWithSectionsById = {};

  answers.forEach(answer => {
    questionsById[answer.questionId] = {
      dbId: answer.questionId,
      sectionId: answer.question.section,
      questionType: answer.question.questionType,
      answers: [
        {
          dbId: answer.dbId,
          date1: answer.date1,
          date2: answer.date2,
          questionText: answer.question.text,
          text: answer.text,
        },
      ],
      text: answer.question.text,
      enumResponses: answer.question.enumResponses,
    };
  });

  return questionsById;
};

const getQuestionsById = (answers: Answer[]) => {
  const questionsById: { [id: number]: Answer } = {};
  for (const answer of answers) {
    questionsById[answer.questionId] = answer;
  }
  return questionsById;
};

const AnswersContext = createContext<{
  answers: ReportAnswers;
  questions: FeedbackQuestionsWithSectionsById;
  isAnswersLoading: boolean;
  answersLoadingError: string | undefined;
  answersById: { [id: number]: Answer };
  updateAnswerRequest: RequestState<unknown>;
  setUpdateAnswerRequest: (
    request: RequestState<
      SuccessResponse<'updateNpoAnswer'> | SuccessResponse<'newNpoAnswer'>
    >,
  ) => void;
  reloadAnswers: () => unknown;
  updateAnswer: (
    a: Omit<UpdateAnswer, 'reportId'>,
    s: FeedbackSectionId,
  ) => unknown;
  updateAnswerPromise: (
    a: Omit<UpdateAnswer, 'reportId'>,
    s: FeedbackSectionId,
  ) => Promise<Omit<UpdateAnswer, 'reportId'>>;
}>({
  answers: {},
  questions: {},
  answersById: {},
  isAnswersLoading: true,
  updateAnswer: () => null,
  reloadAnswers: () => null,
  answersLoadingError: undefined,
  updateAnswerRequest: requestInit(),
  setUpdateAnswerRequest: () => null,
  updateAnswerPromise: () => Promise.reject(),
});

export const useAnswers = () => useContext(AnswersContext);

export const AnswersProvider: React.FC<{
  reportId: string | number;
  children: React.ReactNode;
}> = ({ reportId, children }) => {
  const client = useClient();
  reportId = reportId || useParams<{ feedbackId: string }>().feedbackId;
  const [
    updateAnswerRequest,
    makeUpdateAnswerRequest,
    setUpdateAnswerRequest,
  ] = useGraphQLRequest<
    SuccessResponse<'updateNpoAnswer'> | SuccessResponse<'newNpoAnswer'>
  >();
  const [answers, setAnswers] = useState<ReportAnswers>({});
  const [requestState, makeRequest] = useGraphQLRequest<{
    allReports: ReportAnswersResponse[];
  }>();

  const reloadAnswers = useCallback(() => {
    makeRequest(getReportAnswers(client!, reportId));
  }, [reportId]);

  useEffect(() => {
    if (requestState.type === 'REQUEST_INIT') {
      reloadAnswers();
    } else if (requestState.type === 'REQUEST_SUCCESS') {
      setAnswers(
        mapReportBuilderAnswersToFeedbackAnswer(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (requestState.data.allReports[0]?.answers as any[]) || [],
        ),
      );
    }
  }, [requestState]);

  const updateAnswerState = useCallback(
    (a: Omit<UpdateAnswer, 'reportId'>, sectionId: FeedbackSectionId) => {
      setAnswers(s => {
        const newState = { ...s };
        if (!newState[sectionId]) {
          newState[sectionId] = [];
        }
        const sectionAnswers = newState[sectionId]!;
        const index = sectionAnswers.findIndex(
          x => x.dbId === a.answerId || x.questionId === a.questionId,
        );
        if (index !== -1) {
          sectionAnswers[index].text = a.answer;
          sectionAnswers[index].date1 = a.startDate || null;
          sectionAnswers[index].date2 = a.endDate || null;
        }
        return newState;
      });
    },
    [setAnswers],
  );

  const updateAnswerPromiseRequest = useCallback(
    (a: Omit<UpdateAnswer, 'reportId'>, s: FeedbackSectionId) => {
      return updateAnswer(client!, {
        reportId,
        ...a,
      });
    },
    [updateAnswer, client, reportId],
  );

  const updateAnswerPromise = useCallback(
    (a: Omit<UpdateAnswer, 'reportId'>, s: FeedbackSectionId) => {
      return updateAnswerPromiseRequest(a, s).then(r => {
        if ((r.data?.updateNpoAnswer || r.data?.newNpoAnswer).success) {
          updateAnswerState(a, s);
        }
        return a;
      });
    },
    [updateAnswerPromiseRequest, updateAnswerState],
  );

  const handleUpdateAnswer = useCallback(
    (a: Omit<UpdateAnswer, 'reportId'>, s: FeedbackSectionId) => {
      const request = updateAnswerPromiseRequest(a, s);
      makeUpdateAnswerRequest(request);
      request.then(r => {
        if ((r.data?.updateNpoAnswer || r.data?.newNpoAnswer).success) {
          updateAnswerState(a, s);
        }
      });
    },
    [updateAnswerPromiseRequest, updateAnswerState],
  );

  // Show unauthenticated/not found state when the request succeeded but no report was returned
  if (
    requestState.type === 'REQUEST_SUCCESS' &&
    !requestState.data?.allReports[0]
  ) {
    return (
      <AppUnauthenticated
        title="Report not found"
        message="You may not have access to this report or it does not exist."
      />
    );
  }

  const a = fixImpactDetailAnswers(
    requestState.data?.allReports[0].answers || [],
  );

  return (
    <AnswersContext.Provider
      value={{
        answers,
        setUpdateAnswerRequest,
        updateAnswerPromise,
        questions: mapAnswersToQuestions(a),
        answersById: getQuestionsById(a),
        updateAnswer: handleUpdateAnswer,
        reloadAnswers,
        updateAnswerRequest,
        answersLoadingError:
          requestState.type === 'REQUEST_ERROR'
            ? requestState.error.message
            : undefined,
        isAnswersLoading:
          requestState.type === 'REQUEST_INIT' ||
          requestState.type === 'REQUEST_START',
      }}
    >
      {children}
    </AnswersContext.Provider>
  );
};
